import { useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi } from '../personnelApi';
import {
  DISCIPLINARY_ACTION_TYPE_LABELS,
  DISCIPLINARY_CATEGORY,
  DISCIPLINARY_CATEGORY_LABELS,
  DISCIPLINARY_STATUS,
  DISCIPLINARY_STATUS_LABELS,
  isDisciplinaryConcluded,
  type DisciplinaryActionType,
  type DisciplinaryStatus,
  type PersonnelDisciplinaryRecord,
} from '../personnelTypes';
import { sampleDisciplinary } from '../utils/sampleDisciplinary';

/*
  The type options, in the order the enum declares them — sanctions first, in the CSC's order
  of severity, then the matters raised.

  `sort="none"`, because that order IS the information: a list re-sorted alphabetically puts
  Dismissal above Reprimand and stops saying anything about how serious either is. The same
  call the tier ladder's picker makes.

  The CATEGORY rides as each option's sublabel, which is what makes the derivation visible:
  the reader picks a kind and can see, at the moment of picking, which half of the section it
  will file under. Nothing sends it.
*/
const TYPE_OPTIONS = (Object.keys(DISCIPLINARY_ACTION_TYPE_LABELS) as unknown as string[])
  .map((key) => Number(key) as DisciplinaryActionType)
  .sort((a, b) => a - b)
  .map((value) => ({
    value: String(value),
    label: DISCIPLINARY_ACTION_TYPE_LABELS[value],
    sublabel:
      value <= 5
        ? DISCIPLINARY_CATEGORY_LABELS[DISCIPLINARY_CATEGORY.Disciplinary]
        : DISCIPLINARY_CATEGORY_LABELS[DISCIPLINARY_CATEGORY.Administrative],
  }));

const STATUS_OPTIONS = (Object.keys(DISCIPLINARY_STATUS_LABELS) as unknown as string[])
  .map((key) => Number(key) as DisciplinaryStatus)
  .sort((a, b) => a - b)
  .map((value) => ({ value: String(value), label: DISCIPLINARY_STATUS_LABELS[value] }));

/**
 * Records or corrects one disciplinary or administrative record.
 *
 * **Captured from the hospital system's own form on 18 Sep 2026**, and it keeps that form's
 * fields. What it does not keep is the Category picker: the category is read off the type
 * server-side, so asking for it would be asking the reader to answer a question the record
 * already answers — and letting the two disagree.
 *
 * **The resolution date follows the STATUS, and the form says so before the server does.** It
 * is drawn always and disabled while the matter is still running, with the reason in its own
 * description — disabled for STATE, never hidden, because a field that appeared and vanished
 * as the status changed would move every field under it while the reader worked.
 *
 * **Appealed is NOT concluded**, which is the one thing to read twice here: an appeal is the
 * case still running, so it takes no resolution date. `isDisciplinaryConcluded` and the
 * server's `DisciplinaryCategories.IsConcluded` are one rule written twice and must agree, or
 * the form asks for a date the save refuses.
 */
export function EditDisciplinaryModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds one. */
  editing: PersonnelDisciplinaryRecord | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [type, setType] = useState<string | null>(
    editing ? String(editing.type) : null,
  );
  const [status, setStatus] = useState<string>(
    String(editing?.status ?? DISCIPLINARY_STATUS.Open),
  );
  const [caseNumber, setCaseNumber] = useState(editing?.caseNumber ?? '');
  const [subject, setSubject] = useState(editing?.subject ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [incidentDate, setIncidentDate] = useState(editing?.incidentDate ?? '');
  const [dateFiled, setDateFiled] = useState(editing?.dateFiled ?? '');
  const [actionTaken, setActionTaken] = useState(editing?.actionTaken ?? '');
  const [resolutionDate, setResolutionDate] = useState(editing?.resolutionDate ?? '');
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const today = todayCalendarDate();

  const concluded = isDisciplinaryConcluded(Number(status) as DisciplinaryStatus);

  const typeChosen = type !== null && type !== '';
  const subjectFilled = subject.trim().length > 0;

  // A concluded matter needs the day it ended — the server refuses one without.
  const resolutionOk = !concluded || resolutionDate !== '';

  const canSave = typeChosen && subjectFilled && resolutionOk && confirmed && !saving;

  /** Fills every required field, and the optional ones the form can offer a real value for. */
  function generate() {
    const sample = sampleDisciplinary();

    setType(String(sample.type));
    setStatus(String(sample.status));
    setCaseNumber(sample.caseNumber);
    setSubject(sample.subject);
    setDescription(sample.description);
    setIncidentDate(sample.incidentDate);
    setDateFiled(sample.dateFiled);
    setActionTaken(sample.actionTaken);
    setResolutionDate(sample.resolutionDate);
    setRemarks(sample.remarks);
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      type: Number(type) as DisciplinaryActionType,
      status: Number(status) as DisciplinaryStatus,
      subject: subject.trim(),

      // Empty is ABSENT, not a blank value — one meaning for the null downstream.
      caseNumber: caseNumber.trim() || null,
      description: description.trim() || null,
      incidentDate: incidentDate || null,
      dateFiled: dateFiled || null,
      actionTaken: actionTaken.trim() || null,

      /*
        Cleared rather than sent when the matter is still running. The server clears it too —
        this is not a second check, it is what stops a date the reader cannot see being sent
        from a disabled field they have no way to correct.
      */
      resolutionDate: concluded ? resolutionDate || null : null,
      remarks: remarks.trim() || null,
    };

    try {
      if (editing) {
        await personnelApi.updateDisciplinaryRecord(personnelId, editing.id, request);
      } else {
        await personnelApi.addDisciplinaryRecord(personnelId, request);
      }

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);

      /*
        Every server field name kebabs straight onto an input id here — `resolutionDate` becomes
        `disciplinary-resolution-date` — except the two pickers, whose ids are the same shape
        anyway. There is no `<RadioGroup>` on this form, which is the one control that needs a
        `fieldIds` entry because it puts its id on each option rather than on itself.
      */
      focusValidationError(failure, { idPrefix: 'disciplinary' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit record' : 'Add a disciplinary record'}
      size="2xl"
      id="disciplinary-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'disciplinary-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="disciplinary-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="disciplinary-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          FULL WIDTH, outside the grid — this is the field that NAMES the record, and a subject
          runs long ("Repeated late arrival during the night shift"). The shape
          `<EditRecognitionModal>` already has: the identity leads at full width, the facts pair
          in the grid under it.
        */}
        <TextField
          id="disciplinary-subject"
          label="Subject"
          required
          description="What the record is about, in a line. It is what identifies the row in the list — not the type, which reads the same on every warning."
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          error={subjectFilled ? error?.fieldError('subject') : 'Say what this record is about.'}
          disabled={saving}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="disciplinary-type"
            label="Type"
            required
            description="What the record is. The first six are sanctions; the rest are matters raised. Which half it files under is read from this — there is no separate category to choose."
            value={type}
            onChange={setType}
            options={TYPE_OPTIONS}
            /* The enum's own order is the severity ladder — alphabetical would destroy it. */
            sort="none"
            clearable={false}
            placeholder="Choose a type"
            error={typeChosen ? error?.fieldError('type') : 'Choose what this record is.'}
            disabled={saving}
          />

          <TextField
            id="disciplinary-case-number"
            label="Case number"
            description="The number on the document this came from, if it carries one. It is quoted rather than issued here, so two employees named in one case share it."
            className="font-mono"
            value={caseNumber}
            onChange={(e) => setCaseNumber(e.target.value)}
            error={error?.fieldError('caseNumber')}
            disabled={saving}
          />

          <DateField
            id="disciplinary-incident-date"
            label="Incident date"
            description="The day the incident happened. It cannot be in the future."
            max={today}
            value={incidentDate}
            onChange={setIncidentDate}
            error={error?.fieldError('incidentDate')}
            disabled={saving}
          />

          <DateField
            id="disciplinary-date-filed"
            label="Date filed"
            description="The day the matter was formally filed — on or after the incident it concerns."
            max={today}
            value={dateFiled}
            onChange={setDateFiled}
            error={error?.fieldError('dateFiled')}
            disabled={saving}
          />

          <Select
            id="disciplinary-status"
            label="Case status"
            required
            description="Where the matter has got to. This is the CASE's state, not the record's — deleting a record hides it from the file, where Dismissed means the charge did not stand and stays on it."
            value={status}
            onChange={(next) => setStatus(next ?? String(DISCIPLINARY_STATUS.Open))}
            options={STATUS_OPTIONS}
            sort="none"
            clearable={false}
            error={error?.fieldError('status')}
            disabled={saving}
          />

          {/*
            DRAWN ALWAYS, disabled while the matter is still running — disabled for STATE, with
            the reason in its own description. A field that appeared and vanished as the status
            changed would move every field under it while the reader worked.
          */}
          <DateField
            id="disciplinary-resolution-date"
            label="Resolution date"
            required={concluded}
            description={
              concluded
                ? 'The day the matter ended. Required, because the status says it has.'
                : 'Available once the status says the matter has ended. An appeal is the case still running, so it takes no resolution date.'
            }
            max={today}
            value={concluded ? resolutionDate : ''}
            onChange={setResolutionDate}
            disabled={saving || !concluded}
            error={
              resolutionOk
                ? error?.fieldError('resolutionDate')
                : 'Enter the date the matter ended.'
            }
          />
        </div>

        <Textarea
          id="disciplinary-description"
          label="Description"
          description="The narrative — what happened, as the file records it."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={error?.fieldError('description')}
          disabled={saving}
        />

        <Textarea
          id="disciplinary-action-taken"
          label="Action taken"
          description="The sanction imposed, or how the matter was disposed of."
          value={actionTaken}
          onChange={(e) => setActionTaken(e.target.value)}
          error={error?.fieldError('actionTaken')}
          disabled={saving}
        />

        {/* A remark is a free note about the record as a whole, so it takes the whole row. */}
        <TextField
          id="disciplinary-remarks"
          label="Remarks"
          description="Anything else worth saying about this record. Optional."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={error?.fieldError('remarks')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
